import React, { useEffect, useState } from 'react'
import {
  getAllQueryParamsAsObjectFromUrl,
  replaceUrl
} from '../../utils/UrlUtils'
import { isNull, isUndefined } from 'lodash'
import { useParams, useSearchParams } from 'react-router-dom'

import ApiGroupingStringConstants from '../../constants/ApiGroupingStringConstants'
import ContextSwitcherTabs from '../common/DDS/ContextSwitcherTab/ContextSwitcherTab'
import { DDSTypography } from 'den-design-system'
import NoRecord from '../common/NoContent/NoRecord'
import NoRecordImage from '../../assets/images/folder-not-found 1.svg'
import Spinner from '../common/Spinner/Spinner'
import Table from '../common/DDS/Table/Table'
import Tooltip from '../New/Tooltip/Tooltip'
import { getOrgTimeRange } from '../../utils/DateTimeUtils'
import { observer } from 'mobx-react-lite'
import { paginate } from '../../utils/CommonUtils'
import pallete from '../../global/newPallete'
import routeConstants from '../../constants/RouteConstants'
import { useStoreContext } from '../../store/StoreContext'

interface BreadcrumbOption {
  label: React.ReactNode | string
  link: string
}

const ViewAttributes: React.FC = () => {
  const { id } = useParams()
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [tabIndex, setTabIndex] = useState<string>('1')
  const [searchParams, setSearchParams] = useSearchParams()
  const [tableHeaderData, setTableHeaderData] = useState<any>([])
  const [tableParamsData, setTableParamsData] = useState<any>([])
  const [tableQueryData, setTableQueryData] = useState<any>([])
  const [loading, setLoading] = useState<boolean>(false)
  const store = useStoreContext()
  const apiDetails = store.apiDetailsStore.getApiData()

  const columns = [
    {
      columnHeaderStyle: { width: '50%' },
      columnDataStyle: { width: '50%' },
      heading: (
        <div>
          <DDSTypography.Paragraph
            size='para'
            variant='semiBold'
            color={pallete.colors.textDark6}
          >
            {ApiGroupingStringConstants.ATTRIBUTE}
          </DDSTypography.Paragraph>
        </div>
      ),
      dataLabel: 'attribute',
      render: (text: any) => (
        <div className='flex justify-start text-start row items-start'>
          <DDSTypography.Paragraph
            size='para'
            variant='medium'
            color={pallete.colors.textDark4}
          >
            {text || text === 0 ? text : '-'}
          </DDSTypography.Paragraph>
        </div>
      )
    },
    {
      columnHeaderStyle: { width: '50%' },
      columnDataStyle: { width: '50%' },
      heading: (
        <div className='flex justify-start text-start row items-start'>
          <DDSTypography.Paragraph
            size='para'
            variant='semiBold'
            color={pallete.colors.textDark6}
          >
            {ApiGroupingStringConstants.VALUE}
          </DDSTypography.Paragraph>
        </div>
      ),
      dataLabel: 'value',
      render: (text: any) => (
        <div className='flex justify-start text-start row items-start'>
          <Tooltip
            id={`${text}-tooltip`}
            label={text}
            className={`override-tooltip-arrow max-w-[90%] break-all ${
              typeof text === 'string' && text.length > 145 ? text : '!hidden'
            }`}
          >
            <DDSTypography.Paragraph
              size='para'
              variant='medium'
              className='truncate max-w-[500px]'
              color={pallete.colors.textDark4}
            >
              {text ?? '-'}
            </DDSTypography.Paragraph>
          </Tooltip>
        </div>
      )
    }
  ]

  const getHeaders = () => {
    const headers = { ...store.apiDetailsStore.getApiData().headers }
    store.uiStore.setGlobalLoader(true)
    const headersAsArray = Object.entries(headers).map(
      ([key, value], index) => {
        return {
          attribute: key,
          value: value
        }
      }
    )
    setTableHeaderData(headersAsArray)
    setLoading(false)
    store.uiStore.setGlobalLoader(false)
  }

  const getParams = () => {
    const params = { ...store.apiDetailsStore.getApiData().params }
    store.uiStore.setGlobalLoader(true)
    const paramsAsArray = Object.entries(params).map(([key, value], index) => {
      return {
        attribute: key,
        value: value
      }
    })
    setTableParamsData(paramsAsArray)
    setLoading(false)
    store.uiStore.setGlobalLoader(false)
  }

  const getQuery = () => {
    const query = { ...store.apiDetailsStore.getApiData().query }
    store.uiStore.setGlobalLoader(true)
    const queryAsArray = Object.entries(query).map(([key, value], index) => {
      return {
        attribute: key,
        value: value
      }
    })
    setTableQueryData(queryAsArray)
    setLoading(false)
    store.uiStore.setGlobalLoader(false)
  }

  useEffect(() => {
    const pageFromQueryParams = searchParams.get('page')
    if (!isNull(pageFromQueryParams)) {
      setCurrentPage(Number(pageFromQueryParams))
    } else {
      setCurrentPage(1)
    }
    store.uiStore.setGlobalLoader(true)
    setLoading(true)

    getHeaders()
    getParams()
    getQuery()
  }, [store.apiDetailsStore.getApiData()])

  useEffect(() => {
    setLoading(store.apiDetailsStore.getAPILoading())
  }, [store.apiDetailsStore.apiLoading, currentPage])

  const organizations = store.userStore.getOrganizations()
  const foundOrganization = organizations.find(
    (organization: any) =>
      organization.id === store.scopeStore.getOrganizationId()
  )

  useEffect(() => {
    // Get URL parameters
    const tabFromParams = searchParams.get('tab')
    const tabFromUserParam = searchParams.get('userTab')
    const tabFromUrlParam = searchParams.get('urlViewTab')
    const tabFromAllUserParam = searchParams.get('allUserTab')
    const tabFromParamsOverviewUserTab = searchParams.get('apiOverviewTab')

    const orgTimeZone = store.userStore.getOrganizationPreference()?.timeZone

    const { startOfMonth, endOfCurrentDayTimestamp } =
      getOrgTimeRange(orgTimeZone)

    const currentFilters: Record<string, string> = {
      'created-from':
        sessionStorage.getItem('created-from') || String(startOfMonth),
      'created-to':
        sessionStorage.getItem('created-to') ||
        String(endOfCurrentDayTimestamp),
      'time-zone': sessionStorage.getItem('time-zone') || orgTimeZone
    }

    const filterQueryString = new URLSearchParams(currentFilters).toString()

    const apiData = store.apiDetailsStore.getApiData()
    const userName = localStorage.getItem('apiUserName')
    const scope = store.scopeStore.getScope()

    if (isUndefined(apiData?.urlTemplate)) return

    // Create API monitoring link
    const apiMonitoringLink = replaceUrl(
      routeConstants.API_MONITORING,
      scope['organization-id'] as string,
      scope['project-id'] as string,
      scope['environment-id'] as string
    )

    const breadcrumbOptions: BreadcrumbOption[] = [
      {
        label:
          tabFromParams === '1' || tabFromUserParam === '1'
            ? ApiGroupingStringConstants.GROUPED_API
            : ApiGroupingStringConstants.ALL_API,
        link: `${apiMonitoringLink}${
          tabFromUserParam === '1'
            ? `?tab=1&contextSwitcherTab=2&${filterQueryString}`
            : tabFromAllUserParam === '2'
              ? `?tab=2&contextSwitcherTab=2&${filterQueryString}`
              : tabFromParams === '1'
                ? `?tab=1&${filterQueryString}`
                : `?tab=2&${filterQueryString}`
        }`
      }
    ]

    // Add user breadcrumb if applicable
    if (tabFromUserParam === '1' && userName) {
      breadcrumbOptions.push({
        label: userName,
        link: `${replaceUrl(
          routeConstants.GROUPED_USER_DETAILS,
          scope['organization-id'] as string,
          scope['project-id'] as string,
          scope['environment-id'] as string
        ).replace(
          ':id',
          apiDetails.apiUserId as string
        )}?tab=1&${filterQueryString}`
      })
    }

    // Add API details breadcrumb
    if (foundOrganization?.subscription?.trackApiUsage === true) {
      if (tabFromParamsOverviewUserTab) {
        if (
          tabFromParamsOverviewUserTab === '1' ||
          tabFromParamsOverviewUserTab === '2'
        ) {
          breadcrumbOptions.push({
            label:
              tabFromParamsOverviewUserTab === '1'
                ? 'API Logs'
                : 'API Overview',
            link: `${replaceUrl(
              routeConstants.GROUPED_USER_DETAILS,
              scope['organization-id'] as string,
              scope['project-id'] as string,
              scope['environment-id'] as string
            ).replace(':id', apiDetails.apiUserId as string)}?tab=${
              tabFromParamsOverviewUserTab === '1' ? '1' : '2'
            }&${filterQueryString}`
          })
        }
      } else {
        if (tabFromUrlParam === '1' || tabFromUrlParam === '2') {
          breadcrumbOptions.push({
            label: tabFromUrlParam === '1' ? 'API Logs' : 'API Overview',
            link: `${replaceUrl(
              routeConstants.VIEW_GROUPED_API,
              scope['organization-id'] as string,
              scope['project-id'] as string,
              scope['environment-id'] as string
            ).replace(
              ':id',
              apiDetails.apiGroupId as string
            )}?tab=${tabFromUrlParam}&${filterQueryString}`
          })
        }
      }
    }

    // Add URL template and overview breadcrumbs
    breadcrumbOptions.push(
      {
        label: (
          <Tooltip
            id={`${apiData?.urlTemplate}`}
            position='bottom'
            className={`max-w-[80%] ${
              typeof apiData?.urlTemplate === 'string' &&
              apiData?.urlTemplate.length > 20
                ? apiData?.urlTemplate
                : '!hidden'
            }`}
            label={
              <DDSTypography.Paragraph
                size='para'
                variant='regular'
                color={pallete.colors.primary2}
                className='max-w-[900px] break-words'
              >
                {apiData?.urlTemplate}
              </DDSTypography.Paragraph>
            }
          >
            <div className='truncate max-w-[200px]'>
              <DDSTypography.Paragraph
                size='para'
                variant='regular'
                color={pallete.colors.textDark4}
                className='truncate max-w-[200px]'
              >
                {apiData?.urlTemplate || '-'}
              </DDSTypography.Paragraph>
            </div>
          </Tooltip>
        ),
        link:
          tabFromUserParam || tabFromAllUserParam
            ? `${replaceUrl(
                routeConstants.GROUPED_USER_API_LOGS_DETAILS,
                scope['organization-id'] as string,
                scope['project-id'] as string,
                scope['environment-id'] as string
              ).replace(
                ':apiGroupId/:apiUserId',
                `${apiDetails.apiGroupId}/${apiDetails.apiUserId}` as string
              )}?tab=1&${filterQueryString}`
            : `${replaceUrl(
                routeConstants.VIEW_GROUPED_API,
                scope['organization-id'] as string,
                scope['project-id'] as string,
                scope['environment-id'] as string
              ).replace(
                ':id',
                apiDetails.apiGroupId as string
              )}?page=1&${filterQueryString}`
      },
      {
        label: ApiGroupingStringConstants.ATTRIBUTES,
        link: '/'
      }
    )

    // Set breadcrumb options in store
    store.breadcrumbStore.setMultipleBreadcrumbsOptions(breadcrumbOptions)

    // Cleanup function
    return () => {
      store.breadcrumbStore.reset()
    }
  }, [
    searchParams,
    store.apiDetailsStore.getApiData(),
    store.breadcrumbStore,
    store.apiDetailsStore.getUserName(),
    store.userStore,
    store.scopeStore
  ])

  useEffect(() => {
    const tabFromParams = searchParams.get('contextSwitcherTab')
    if (tabFromParams) {
      setTabIndex(tabFromParams.toString())
    }
  }, [])

  const paginatedHederData = paginate(tableHeaderData, currentPage, 10)
  const paginatedParamsData = paginate(tableParamsData, currentPage, 10)
  const paginateQueryData = paginate(tableQueryData, currentPage, 10)

  const ContextSwitcherTab = [
    {
      id: '1',
      label: (
        <div className='w-[88px] px-[16px]'>
          {ApiGroupingStringConstants.HEADERS}
        </div>
      ),
      children: (
        <div>
          {loading ? (
            <Spinner />
          ) : tableHeaderData?.length === 0 ? (
            <NoRecord imageSrc={NoRecordImage} text={'No records found'} />
          ) : (
            <div>
              <Table
                data={paginatedHederData}
                loading={loading}
                columns={columns}
                totalCount={tableHeaderData?.length}
                rowsPerPage={10}
                currentPage={currentPage}
                tableStyle={{
                  borderColor: pallete.colors.stroke2
                }}
                onPageChange={(page: number) => {
                  const allQueryParams = getAllQueryParamsAsObjectFromUrl(
                    location.search
                  )
                  setSearchParams({ ...allQueryParams, page: page.toString() })
                  setCurrentPage(page)
                }}
                className={`!border-[${pallete.colors.stroke2}] `}
              />
            </div>
          )}
        </div>
      )
    },
    {
      id: '2',
      label: (
        <div className='w-[82px] px-[16px]'>
          {ApiGroupingStringConstants.PARAMS}
        </div>
      ),
      children: (
        <div>
          {loading ? (
            <Spinner />
          ) : tableParamsData?.length === 0 ? (
            <NoRecord imageSrc={NoRecordImage} text={'No records found'} />
          ) : (
            <div>
              <Table
                data={paginatedParamsData}
                columns={columns}
                totalCount={tableParamsData?.length}
                rowsPerPage={10}
                currentPage={currentPage}
                tableStyle={{
                  borderColor: pallete.colors.stroke2
                }}
                onPageChange={(page: number) => {
                  const allQueryParams = getAllQueryParamsAsObjectFromUrl(
                    location.search
                  )
                  setSearchParams({ ...allQueryParams, page: page.toString() })
                  setCurrentPage(page)
                }}
                className={`!border-[${pallete.colors.stroke2}] `}
                loading={loading}
              />
            </div>
          )}
        </div>
      )
    },
    {
      id: '3',
      label: (
        <div className='w-[72px] px-[16px]'>
          {ApiGroupingStringConstants.QUERY}
        </div>
      ),
      children: (
        <div>
          {loading ? (
            <Spinner />
          ) : tableQueryData?.length === 0 ? (
            <NoRecord imageSrc={NoRecordImage} text={'No records found'} />
          ) : (
            <div>
              <Table
                data={paginateQueryData}
                columns={columns}
                totalCount={tableQueryData?.length}
                rowsPerPage={10}
                currentPage={currentPage}
                loading={loading}
                tableStyle={{
                  borderColor: pallete.colors.stroke2
                }}
                onPageChange={(page: number) => {
                  const allQueryParams = getAllQueryParamsAsObjectFromUrl(
                    location.search
                  )
                  setSearchParams({ ...allQueryParams, page: page.toString() })
                  setCurrentPage(page)
                }}
                className={`!border-[${pallete.colors.stroke2}] `}
              />
            </div>
          )}
        </div>
      )
    }
  ]

  return (
    <div>
      <div className='flex h-full w-full'>
        <div className='py-[24px] w-full '>
          <ContextSwitcherTabs
            tabOptions={ContextSwitcherTab}
            shape='roundedRectangle'
            tabStyle={{
              border: '1px solid',
              borderColor: pallete.colors.stroke2,
              userSelect: 'none',
              width: '251px'
            }}
            tabTitleClassName='w-[88px] text-center select-none'
            activeTab={tabIndex}
            onChange={(index: any) => {
              setTabIndex(index.toString())
              const queryParams = getAllQueryParamsAsObjectFromUrl(
                location.search
              )
              setCurrentPage(1)
              setSearchParams({
                ...queryParams,
                page: '1',
                contextSwitcherTab: index.toString()
              })
            }}
            className='override-height'
          />
        </div>
      </div>
    </div>
  )
}

export default observer(ViewAttributes)
